import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Chip,
  Link,
  Grid,
  IconButton,
  InputAdornment,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Language as LanguageIcon,
  Computer as ComputerIcon,
  PhoneAndroid as PhoneIcon,
  Save as SaveIcon,
  OpenInNew as OpenInNewIcon,
  ContentCopy as ContentCopyIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { backendApiService } from '../../services/backendApiService';

interface SearchResult {
  position: number;
  title: string;
  link: string;
  snippet: string;
  displayed_link: string;
}

interface SearchResponse {
  keyword: string;
  country: string;
  device: string;
  search_url: string;
  search_metadata: {
    total_results: string;
    time_taken: string;
    query_displayed: string;
    google_url: string;
  };
  organic_results: SearchResult[];
  people_also_ask?: Array<{
    question: string;
    answer: string;
    link: string;
  }>;
  related_searches?: Array<{
    query: string;
  }>;
}

const countries = [
  { code: 'UK', name: 'United Kingdom', flag: '🇬🇧' },
  { code: 'NL', name: 'Netherlands', flag: '🇳🇱' },
  { code: 'FR', name: 'France', flag: '🇫🇷' },
  { code: 'DE', name: 'Germany', flag: '🇩🇪' },
  { code: 'IT', name: 'Italy', flag: '🇮🇹' },
  { code: 'ES', name: 'Spain', flag: '🇪🇸' },
  { code: 'SE', name: 'Sweden', flag: '🇸🇪' },
  { code: 'NO', name: 'Norway', flag: '🇳🇴' },
  { code: 'DK', name: 'Denmark', flag: '🇩🇰' },
  { code: 'FI', name: 'Finland', flag: '🇫🇮' },
  { code: 'BR', name: 'Brazil', flag: '🇧🇷' },
  { code: 'JP', name: 'Japan', flag: '🇯🇵' },
  { code: 'CA', name: 'Canada', flag: '🇨🇦' },
  { code: 'AU', name: 'Australia', flag: '🇦🇺' },
  { code: 'KR', name: 'South Korea', flag: '🇰🇷' },
  { code: 'RU', name: 'Russia', flag: '🇷🇺' },
  { code: 'CN', name: 'China', flag: '🇨🇳' },
  { code: 'COM_US', name: 'United States (.com)', flag: '🇺🇸' },
  { code: 'COM_MX', name: 'Mexico (.com)', flag: '🇲🇽' },
  { code: 'COM_AR', name: 'Argentina (.com)', flag: '🇦🇷' },
  { code: 'COM_CL', name: 'Chile (.com)', flag: '🇨🇱' },
];

// Our website domains to detect
const OUR_DOMAINS = [
  'newyork.co.uk',
  'newyork.nl',
  'newyorkcity.de',
  'newyorkcity.fr',
  'newyork.com.br',
  'newyorkcity.fi',
  'newyork.se',
  'newyorkcity.it',
  'newyorkcity.jp',
  'newyorkcity.es',
  'www.newyork.com',
  'newyork.ca',
  'newyork.com.au',
  'newyork.no',
  'newyork.co.kr',
  'newyorkcity.dk',
  'newyork.ru',
  'newyorkcity.cn'
];

// Official websites that can't be beaten (priority 999)
// These should match your website_competitors table with priority 999
const OFFICIAL_DOMAINS = [
  // Official sports sites
  'nba.com',
  'ticketmaster.com',
  'ticketmaster.co.uk',
  'stubhub.com',
  'seatgeek.com',
  'vividseats.com',
  'viagogo.com',
  'tickpick.com',
  // Travel & Tourism
  'tripadvisor.com',
  'tripadvisor.co.uk',
  'viator.com',
  'getyourguide.com',
  'booking.com',
  'expedia.com',
  'hotels.com',
  'airbnb.com',
  'timeout.com',
  'lonelyplanet.com',
  'wikipedia.org',
  'wikivoyage.org',
  '.gov',
  'nps.gov',
  'nyc.gov',
];

export const RankingOnDemandPage: React.FC = () => {
  const [keyword, setKeyword] = useState('');
  const [country, setCountry] = useState('UK');
  const [device, setDevice] = useState<'desktop' | 'mobile'>('desktop');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<SearchResponse | null>(null);
  const [copiedUrl, setCopiedUrl] = useState(false);

  // Check if a URL belongs to one of our websites
  const isOurWebsite = (url: string): boolean => {
    const lowerUrl = url.toLowerCase();
    return OUR_DOMAINS.some(domain => {
      const lowerDomain = domain.toLowerCase();
      // Check both with and without www
      return lowerUrl.includes(lowerDomain) || 
             lowerUrl.includes('www.' + lowerDomain) ||
             lowerUrl.includes(lowerDomain.replace('www.', ''));
    });
  };

  // Check if a URL is an official website (priority 999)
  const isOfficialWebsite = (url: string): boolean => {
    const lowerUrl = url.toLowerCase();
    return OFFICIAL_DOMAINS.some(domain => lowerUrl.includes(domain.toLowerCase()));
  };

  // Copy URL to clipboard
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleSearch = async () => {
    if (!keyword.trim()) {
      setError('Please enter a keyword to search');
      return;
    }

    setLoading(true);
    setError(null);
    setResults(null);

    try {
      // Debug: Log the exact URL being used
      console.log('Attempting to call Serphouse API...');
      console.log('Backend service base URL:', process.env.REACT_APP_API_URL);
      
      const response = await backendApiService.post('/api/serphouse/search', {
        keyword: keyword.trim(),
        country,
        device,
      });

      if (response.success) {
        setResults(response.data);
      } else {
        setError(response.error || 'Failed to fetch search results');
      }
    } catch (err: any) {
      console.error('Serphouse API error:', err);
      setError(err.response?.data?.error || 'An error occurred while fetching results');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const handleRelatedSearch = (query: string) => {
    setKeyword(query);
    // Auto-search when clicking related search
    setTimeout(() => handleSearch(), 100);
  };


  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom fontWeight={600}>
          Ranking on Demand
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Check real-time Google rankings for any keyword instantly
        </Typography>
      </Box>

      {/* Search Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              label="Keyword"
              variant="outlined"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Enter keyword to search..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Country</InputLabel>
              <Select
                value={country}
                label="Country"
                onChange={(e) => setCountry(e.target.value)}
                startAdornment={
                  <InputAdornment position="start">
                    <LanguageIcon color="action" />
                  </InputAdornment>
                }
              >
                {countries.map((c) => (
                  <MenuItem key={c.code} value={c.code}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>{c.flag}</span>
                      <span>{c.name}</span>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2}>
            <ToggleButtonGroup
              value={device}
              exclusive
              onChange={(e, newDevice) => newDevice && setDevice(newDevice)}
              fullWidth
            >
              <ToggleButton value="desktop" aria-label="desktop">
                <ComputerIcon />
              </ToggleButton>
              <ToggleButton value="mobile" aria-label="mobile">
                <PhoneIcon />
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>

          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSearch}
              disabled={loading}
              sx={{ height: '56px' }}
            >
              {loading ? <CircularProgress size={24} /> : 'Search'}
            </Button>
          </Grid>
        </Grid>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Paper>

      {/* Results Section */}
      {results && (
        <Box>
          {/* Search URL Section */}
          <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <VisibilityIcon color="action" />
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Google Search URL
                </Typography>
                <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                  {results.search_metadata.google_url || 'URL not available'}
                </Typography>
              </Box>
              <Tooltip title={copiedUrl ? 'Copied!' : 'Copy URL'}>
                <IconButton
                  onClick={() => copyToClipboard(results.search_metadata.google_url)}
                  size="small"
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Chip
                label={`${results.search_metadata.total_results} results`}
                size="small"
                variant="outlined"
              />
              <Chip
                label={`${results.search_metadata.time_taken} seconds`}
                size="small"
                variant="outlined"
              />
              <Chip
                label={device === 'mobile' ? 'Mobile' : 'Desktop'}
                size="small"
                variant="outlined"
                icon={device === 'mobile' ? <PhoneIcon /> : <ComputerIcon />}
              />
            </Box>
          </Paper>


          {/* Main Results Grid */}
          <Grid container spacing={3}>
            {/* Left Column - Organic Results */}
            <Grid item xs={12} md={8}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6" fontWeight={600}>
                    Organic Search Results
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<SaveIcon />}
                    onClick={() => console.log('Save results')}
                    size="small"
                  >
                    Save Results
                  </Button>
                </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {results.organic_results?.map((result, index) => {
                const isOurs = isOurWebsite(result.link);
                const isOfficial = isOfficialWebsite(result.link);
                // Smart fold detection: 
                // Desktop: Usually around 6-8 organic results above fold (after ads/features)
                // Mobile: Usually around 3-4 organic results above fold
                const foldPosition = device === 'mobile' ? 3 : 6;
                const isFold = index === foldPosition;
                
                // Debug logging
                if (result.link.includes('newyork')) {
                  console.log('Found our website!', result.link, 'isOurs:', isOurs);
                }

                return (
                  <React.Fragment key={index}>
                    {isFold && (
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 2, 
                        my: 2,
                        color: 'text.secondary' 
                      }}>
                        <Divider sx={{ flex: 1 }} />
                        <Typography variant="caption" sx={{ px: 2 }}>
                          📱 THE FOLD - Most users don't scroll past here
                        </Typography>
                        <Divider sx={{ flex: 1 }} />
                      </Box>
                    )}
                    <Card 
                      variant="outlined"
                      sx={{
                        position: 'relative',
                        border: isOurs ? '2px solid' : isOfficial ? '1px solid' : '1px solid',
                        borderColor: isOurs ? 'primary.main' : isOfficial ? 'error.light' : 'divider',
                        bgcolor: isOurs ? 'primary.50' : isOfficial ? 'error.50' : 'background.paper',
                        animation: isOurs ? 'pulse 2s infinite' : 'none',
                        '@keyframes pulse': {
                          '0%': { boxShadow: '0 0 0 0 rgba(25, 118, 210, 0.4)' },
                          '70%': { boxShadow: '0 0 0 10px rgba(25, 118, 210, 0)' },
                          '100%': { boxShadow: '0 0 0 0 rgba(25, 118, 210, 0)' },
                        },
                      }}
                    >
                      {isOurs && (
                        <Chip
                          label="🎉 OUR WEBSITE! ⭐"
                          color="primary"
                          size="small"
                          sx={{ 
                            position: 'absolute', 
                            top: 8, 
                            right: 8,
                            fontWeight: 600,
                          }}
                        />
                      )}
                      {isOfficial && !isOurs && (
                        <Box
                          sx={{ 
                            position: 'absolute', 
                            top: 8, 
                            right: 8,
                            backgroundColor: 'error.main',
                            color: 'white',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 2,
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          🏆 OFFICIAL - CAN'T BEAT
                        </Box>
                      )}
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'start', gap: 2 }}>
                          <Chip
                            label={result.position}
                            color={isOurs ? 'primary' : 'default'}
                            size="small"
                            sx={{ 
                              minWidth: 32, 
                              fontWeight: 600,
                              bgcolor: isOurs ? 'primary.main' : undefined,
                              color: isOurs ? 'white' : undefined,
                            }}
                          />
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Link
                                href={result.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                underline="hover"
                                variant="h6"
                                sx={{ 
                                  fontWeight: isOurs ? 600 : 500,
                                  color: isOurs ? 'primary.main' : 'text.primary',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                }}
                              >
                                {result.title}
                                <OpenInNewIcon sx={{ fontSize: 16 }} />
                              </Link>
                            </Box>
                            <Typography 
                              variant="body2" 
                              color={isOurs ? 'primary.main' : 'success.main'} 
                              gutterBottom
                              sx={{ fontWeight: isOurs ? 600 : 400 }}
                            >
                              {result.displayed_link}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {result.snippet}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </React.Fragment>
                );
              })}
            </Box>
              </Paper>
            </Grid>

            {/* Right Column - PAA and Related Searches */}
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* People Also Ask */}
                {results.people_also_ask && results.people_also_ask.length > 0 && (
                  <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      ❓ People Also Ask
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {results.people_also_ask.slice(0, 4).map((item, index) => (
                        <Box key={index}>
                          <Typography variant="subtitle2" fontWeight={500}>
                            {item.question}
                          </Typography>
                          {item.answer && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              {item.answer.substring(0, 100)}...
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Paper>
                )}

                {/* Related Searches */}
                {results.related_searches && results.related_searches.length > 0 && (
                  <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      🔍 Related Searches
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {results.related_searches.slice(0, 8).map((item, index) => (
                        <Chip
                          key={index}
                          label={item.query}
                          onClick={() => handleRelatedSearch(item.query)}
                          clickable
                          size="small"
                          sx={{ justifyContent: 'flex-start' }}
                        />
                      ))}
                    </Box>
                  </Paper>
                )}

                {/* Quick Links */}
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    🔗 Quick Actions
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<OpenInNewIcon />}
                      href={results.search_metadata?.google_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View on Google
                    </Button>
                    <Divider />
                    <Typography variant="caption" color="text.secondary">
                      Total Results: {results.search_metadata.total_results}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Search Time: {results.search_metadata.time_taken}
                    </Typography>
                  </Box>
                </Paper>
              </Box>
            </Grid>
          </Grid>

        </Box>
      )}
    </Box>
  );
};